import { registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { platform } from './identity';
import { registerPushDevice, revokePushDevice } from '../services/pushApi';

const DEVICE_KEY = 'z10:push-device-id';
let handles = [];
let initialization = null;
let subscribers = 0;
let actionHandles = [];
let actionInitialization = null;
let actionSubscribers = 0;
const TAP_STAGES = new Set(['action_received', 'route_resolved', 'auth_ready', 'navigation_requested', 'chat_route_rendered']);
// Auth bootstrap is local persistence restoration; there is no HTTP restore
// request in this path, so do not advertise a misleading request stage.
const AUTH_STAGES = new Set(['provider_mounted', 'persistence_probe_started', 'persistence_found', 'persistence_absent', 'restore_succeeded', 'restore_unauthenticated', 'restore_failed', 'user_state_ready', 'auth_ready']);
const TAP_DIAGNOSTICS_ENABLED = process.env.REACT_APP_ANDROID_ENV === 'development';
const ColdStartRoute = registerPlugin('ZodiacusColdStartRoute');

export function emitNotificationTapStage(stage, onTapStage) {
  if (!TAP_STAGES.has(stage)) return;
  try { onTapStage?.(stage); } catch {}
  try { window.dispatchEvent(new CustomEvent('zodiacus:notificationTapStage', { detail: { stage } })); } catch {}
  if (TAP_DIAGNOSTICS_ENABLED) {
    console.info('[push]', { event: 'notification_tap_stage', stage });
    // WebView console output is not consistently mirrored to adb logcat. The
    // app-owned plugin accepts only fixed stage names and logs them in DEBUG.
    try { ColdStartRoute?.recordDebugStage?.({ stage }).catch(() => {}); } catch {}
  }
}

export function emitAuthRestoreStage(stage) {
  if (!AUTH_STAGES.has(stage)) return;
  if (!TAP_DIAGNOSTICS_ENABLED) return;
  try { ColdStartRoute?.recordDebugStage?.({ stage: `auth_restore_stage=${stage}` }).catch(() => {}); } catch {}
}

function deviceId(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  let id = storage?.getItem?.(DEVICE_KEY) || '';
  if (!id) {
    const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
    if (cryptoApi?.randomUUID) id = cryptoApi.randomUUID();
    else if (cryptoApi?.getRandomValues) {
      const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
      id = `android-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
    } else id = `android-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    try { storage?.setItem?.(DEVICE_KEY, id); } catch {}
  }
  return id;
}

export function notificationRoute(notification = {}) {
  let route = String(notification?.data?.route || notification?.notification?.data?.route || '').trim();
  try { route = decodeURIComponent(route); } catch { return '/home'; }
  const safe = /^\/(?:home|contacts|foro|foro\/[^/?#]+|foro\/[^/?#]+\/nuevo|foro\/tema\/[^/?#]+|foro\/thread\/[^/?#]+|profile(?:\/[^/?#]+)?|chat(?:\/[^/?#]+)?|dm\/[^/?#]+|rooms(?:\/[^/?#]+)?|room\/[^/?#]+|ruletas|zodiac-roulette|filtered-roulette\/[^/?#]+|notification-preferences)$/;
  return route.length <= 300 && safe.test(route) && !route.includes('://') ? route : '/home';
}

// Register notification actions before authentication is restored. This keeps
// a cold-launch action available to PlatformRuntime without registering an FCM
// device for an anonymous session.
export async function initializePushNotificationActions({
  onRoute,
  onTapStage,
  plugin = PushNotifications,
  coldStartPlugin = ColdStartRoute,
  native = platform.isAndroid,
} = {}) {
  if (!native) return () => {};
  actionSubscribers += 1;
  const cleanup = () => {
    actionSubscribers = Math.max(0, actionSubscribers - 1);
    if (actionSubscribers) return;
    actionHandles.splice(0).forEach((handle) => { try { handle?.remove?.(); } catch {} });
    actionInitialization = null;
  };
  try {
    if (!actionInitialization) {
      actionInitialization = (async () => {
        let processedActionRoute = null;
        const handle = await plugin.addListener('pushNotificationActionPerformed', ({ notification }) => {
          const route = notificationRoute(notification);
          if (route === processedActionRoute) return;
          processedActionRoute = route;
          emitNotificationTapStage('action_received', onTapStage);
          emitNotificationTapStage('route_resolved', onTapStage);
          onRoute?.(route);
        });
        actionHandles.push(handle);

        // Firebase display notifications launch the app with application data in
        // the initial Intent. Read the app-owned one-shot bridge only after the
        // stock listener is attached, so a working Capacitor action wins and the
        // two paths cannot navigate twice.
        const initial = await coldStartPlugin?.consumeInitialRoute?.().catch(() => null);
        const route = initial?.route ? notificationRoute({ data: { route: initial.route } }) : null;
        if (route && route !== '/home' && route !== processedActionRoute) {
          processedActionRoute = route;
          emitNotificationTapStage('action_received', onTapStage);
          emitNotificationTapStage('route_resolved', onTapStage);
          onRoute?.(route);
        }
      })();
    }
    await actionInitialization;
    return cleanup;
  } catch (error) {
    cleanup();
    throw error;
  }
}

export async function initializePushNotifications({ navigate, onTapStage, plugin = PushNotifications, native = platform.isAndroid } = {}) {
  if (!native) return () => {};
  subscribers += 1;
  const cleanup = () => {
    subscribers = Math.max(0, subscribers - 1);
    if (subscribers) return;
    handles.splice(0).forEach((handle) => { try { handle?.remove?.(); } catch {} });
    initialization = null;
  };
  try {
    if (!initialization) {
      initialization = (async () => {
        const id = deviceId();
        const add = async (event, handler) => { const handle = await plugin.addListener(event, handler); handles.push(handle); };
        await add('registration', ({ value }) => registerPushDevice({ token: value, deviceId: id, platform: 'android' }).catch(() => {}));
        await add('registrationError', () => {});
        await add('pushNotificationReceived', (notification) => {
          try { window.dispatchEvent(new CustomEvent('zodiacus:pushReceived', { detail: { type: notification?.data?.type || 'future' } })); } catch {}
        });
        await plugin.createChannel({ id: 'zodiacus_messages', name: 'Messages', description: 'Private message notifications', importance: 4, visibility: 1 });
        await plugin.createChannel({ id: 'zodiacus_activity', name: 'Zodiacus activity', description: 'Forum, reaction, moderation and announcement notifications', importance: 3, visibility: 1 });
        let permission = await plugin.checkPermissions();
        if (permission.receive === 'prompt') permission = await plugin.requestPermissions();
        if (permission.receive === 'granted') await plugin.register();
      })();
    }
    await initialization;
    return cleanup;
  } catch (error) {
    cleanup();
    throw error;
  }
}

export async function revokeCurrentPushDevice() {
  if (!platform.isAndroid) return false;
  const id = deviceId();
  try { await revokePushDevice(id); await PushNotifications.unregister(); return true; } catch { return false; }
}

export const pushDeviceIdForTests = deviceId;
